import React, { useState, useCallback } from 'react'
import { styled } from '@mui/material/styles'
import {
  FormDataConsumer,
  ExportButton,
  SimpleFormIterator,
  ArrayInput,
  useTranslate,
  TextInput,
} from 'react-admin'
import { CustomFormProps, FormDataConsumerProps } from 'types/common'
import { equals } from 'services/utils'
import { exportRecord } from 'services/csv/exporter'
import { CustomRecordForAggregates, CustomRecord } from './computedGetters'
import FormService from './FormService'
import FormGeneralSection from './FormGeneralSection'
import CustomAutocompleteInput from 'ui-shared/CustomInputs/CustomAutocompleteInput'
import Kdivider from 'ui-shared/LayoutComponents/Kdivider'
import { optionsDate } from 'services/utils/date'
import { getGeoLang } from 'services/geo-management'
import Kheader from 'ui-shared/LayoutComponents/Kheader'
import { Divider } from '@mui/material'
import { APIEntity } from 'services/api/entities'
import LabeledNumberField from 'ui-shared/LabeledFields/LabeledNumberField'
import LabeledDateField from 'ui-shared/LabeledFields/LabeledDateField'
import LabeledTextField from 'ui-shared/LabeledFields/LabeledTextField'
import ConfirmedTransactionFields from './ConfirmedTransactionFields'
import TransactionCustomizationsInput from './TransactionCustomizationsInput'
import { useKRecordContext } from 'services/hooks/useKRecordContext'
import KAccordion from 'ui-shared/accordions/KAccordion'
import {
  Transaction,
  TransactionService,
} from 'services/api/entities/accounting/transaction/types/internal/transaction-models'
import { NAMED_SERVICES } from 'services/api/entities/accounting/transaction'
import { KSimpleForm } from 'ui-shared/WrappedInputs/KSimpleForm'

const PREFIX = 'TransactionForm'

const classes = {
  inline: `${PREFIX}-inline`,
  divider: `${PREFIX}-divider`,
  column: `${PREFIX}-column`,
  withoutVATLabel: `${PREFIX}-withoutVATLabel`,
  exportPadding: `${PREFIX}-exportPadding`,
  dNone: `${PREFIX}-dNone`,
}

const Root = styled('div')(({ theme }: any) => ({
  [`& .${classes.inline}`]: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
  },

  [`& .${classes.divider}`]: {
    width: '100%',
    height: '3px',
    backgroundColor: theme.palette.primary.light,
    marginTop: '20px',
  },

  [`& .${classes.column}`]: {
    display: 'flex',
    minWidth: '30%',
    margin: '0px 24px',
    flexDirection: 'column',
  },

  [`& .${classes.withoutVATLabel}`]: {
    width: '100%',
    marginTop: '10px',
  },

  [`& .${classes.exportPadding}`]: {
    padding: '10px',
  },

  [`& .${classes.dNone}`]: {
    display: 'none',
  },
}))

type Props = CustomFormProps & {
  resource?: APIEntity
}

export const roundTwoDecimals = {
  maximumFractionDigits: 2,
}

const TransactionForm: React.FC<Props> = (props) => {
  const [servicesTotals, setServicesTotals] = useState<
    Record<string, CustomRecordForAggregates>
  >(Object.create(null))

  const aggregateRecord = Object.values(servicesTotals)

  const translate = useTranslate()

  const callUpdateState = useCallback(
    (partialRecord: CustomRecord) => {
      // NOTE: we DO NOT set the state if no change occurred on child-handled data => this avoids infinite loop
      // TODO: investigate to find a cleaner, more universal way to handle these use cases!
      if (!equals(partialRecord, servicesTotals[partialRecord.serviceName])) {
        setServicesTotals({
          ...servicesTotals,
          [partialRecord.serviceName]: { ...partialRecord },
        })
      }
    },
    [servicesTotals]
  )

  const lang = getGeoLang()

  const record = useKRecordContext<Transaction>()

  const isEventConfirmed = record && record.eventStatus === 'confirmed'

  return (
    <Root>
      <div className={classes.exportPadding}>
        <ExportButton onClick={() => exportRecord(props)} />
      </div>
      <KSimpleForm {...props}>
        <Kheader title="General" headingLevel="h1" />
        <Divider />
        <LabeledNumberField source="id" label="form.fields.eventId" />

        <LabeledTextField
          source="eventStatus"
          label="form.fields.eventStatus"
        />
        {isEventConfirmed && <ConfirmedTransactionFields />}

        <LabeledTextField source="eventType" label="form.fields.eventType" />
        <LabeledDateField
          source="confirmedAt"
          label="form.fields.confirmedAt"
          locales={lang}
          options={optionsDate}
        />
        <LabeledDateField
          source="eventDate"
          label="form.fields.eventDate"
          locales={lang}
          options={optionsDate}
        />
        <LabeledTextField
          source="monthYearDate"
          label="form.fields.monthYearDate"
        />
        <LabeledTextField
          source="customerName"
          label="form.fields.clientFullName"
        />
        <LabeledTextField
          source="customerEmail"
          label="form.fields.clientEmail"
        />
        <LabeledTextField
          source="eventAddress"
          label="form.fields.eventAddress"
        />
        <LabeledTextField
          source="customerAddress"
          label="form.fields.clientAddress"
        />
        <LabeledTextField
          label="form.fields.businessCountry"
          source="customerBusinessCountry"
        />
        <LabeledTextField
          label="form.fields.paymentMethod"
          source="paymentMethod"
        />
        <TextInput source="poNumber" />
        <LabeledTextField source="customerVat" label="form.fields.clientVat" />
        <LabeledTextField
          source="customerFiscalCode"
          label="form.fields.clientFiscalCode"
        />
        <LabeledTextField source="customerSdi" label="form.fields.clientSdi" />
        <LabeledTextField
          source="customerPhone"
          label="form.fields.clientPhone"
        />
        <LabeledTextField source="customerPec" label="form.fields.clientPec" />

        <CustomAutocompleteInput
          entity="coupon"
          optionValue="code"
          source="couponCode"
          label="Coupon Code"
        />
        <Kheader title="Event Totals" headingLevel="h1" />
        <Divider />

        <FormDataConsumer>
          {({
            formData: {
              id,
              discountPerc,
              customerBusinessCountry,
              customizations,
            },
          }: {
            formData: Transaction
          }) => (
            <>
              <FormGeneralSection
                discountPerc={discountPerc}
                formId={id}
                aggregateRecord={aggregateRecord}
                customizations={customizations}
                customerBusinessCountry={customerBusinessCountry}
              />
              {!!customizations?.length && <TransactionCustomizationsInput />}
            </>
          )}
        </FormDataConsumer>

        <Kdivider title="Services" headingLevel="h1" />

        <ArrayInput source="services">
          <SimpleFormIterator disableRemove disableAdd disableReordering>
            <FormDataConsumer>
              {({
                formData,
                scopedFormData,
                getSource,
              }: FormDataConsumerProps<Transaction, TransactionService>) => {
                if (!scopedFormData) return null

                // NOTE: if we want to replicate this, extract it in a `kiddingReactAdmin` util
                const baseSource = getSource!('')
                getSource = (src: string) => `${baseSource}.${src}`

                const { id, customerBusinessCountry } = formData
                const { serviceName } = scopedFormData

                return (
                  <>
                    <KAccordion
                      accordionTitle={
                        NAMED_SERVICES.some((n) => n === serviceName)
                          ? translate(`form.headers.${serviceName}`)
                          : serviceName
                      }
                      defaultExpanded={
                        scopedFormData.price > 0 ||
                        !!scopedFormData.customization
                      }
                    >
                      <FormService
                        formId={id}
                        partialFormData={scopedFormData}
                        getSource={getSource!}
                        setServiceRecord={callUpdateState}
                        customerBusinessCountry={customerBusinessCountry}
                      />
                    </KAccordion>
                  </>
                )
              }}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      </KSimpleForm>
    </Root>
  )
}

export default TransactionForm
